import { createRouter, createWebHashHistory } from "vue-router";
import TutorialView from "@/views/TutorialView.vue";
import TermsOfUse from "@/views/TermsOfUseView.vue";
import SkinAnalysis from "@/views/SkinAnalysisView.vue";
import AppDownloadGuideView from "@/views/AppDownloadGuideView.vue";
import UnsupportedView from "@/views/UnsupportedView.vue";
import MaintenanceView from "@/views/MaintenanceView.vue";
import SmoothepiTermsOfUseView from "@/views/terms-of-use/SmoothepiTermsOfUseView.vue";
import FacecareTermsOfUseView from "@/views/terms-of-use/FacecareTermsOfUseView.vue";
import DoltzTermsOfUseView from "@/views/terms-of-use/DoltzTermsOfUseView.vue";
import PlaqueTermsOfUseView from "@/views/terms-of-use/PlaqueTermsOfUseView.vue";
import fetchAPI from "@/utils/fetch";
import consoleController from "@/utils/logUtil";
import errorUtil from "@/utils/errorUtil";
import store from "@/utils/store";
import constant from "@/const";
import i18n from "@/i18n";

// メンテナンス情報の取得
let isMaintenance = false;
let maintenanceInfo = {};
export async function checkMaintenance(lang) {
  try {
    const { fetch } = fetchAPI;
    const path = "/web/config/maintenance";
    let queryParams = null;
    if (lang !== "") {
      queryParams = {
        lang,
      };
    }
    const res = await fetch.fetchAPI("GET", path, queryParams, null);

    if (res != undefined && res.status === 200) {
      maintenanceInfo = await res.json();
      // メンテナンス状態
      isMaintenance = maintenanceInfo.maintenanceFlag;
    } else if (res !== undefined && res.status !== 200) {
      const errorInfo = await res.json();
      errorUtil.createErrorDialog(errorInfo);
      consoleController(
        "error",
        `${path} is failed. errorInfo: ${JSON.stringify(errorInfo, null, 4)}`,
      );
      maintenanceInfo = {};
      isMaintenance = true;
    } else {
      errorUtil.createErrorDialog(undefined);
      consoleController("error", `${path} is failed.}`);
      maintenanceInfo = {};
      isMaintenance = true;
    }
  } catch (e) {
    consoleController("log", e);
  }
}

const routes = [
  {
    path: "/",
    name: "TutorialView",
    component: TutorialView,
  },
  {
    path: "/terms-of-use",
    name: "TermsOfUse",
    component: TermsOfUse,
  },
  {
    path: "/skin-analysis",
    name: "SkinAnalysis",
    component: SkinAnalysis,
  },
  {
    path: "/download",
    name: "AppDownloadGuide",
    component: AppDownloadGuideView,
  },
  {
    path: "/unsupported",
    name: "Unsupported",
    component: UnsupportedView,
  },
  {
    path: "/maintenance",
    name: "Maintenance",
    component: MaintenanceView,
    props: () => ({ maintenanceInfo }),
  },
  {
    path: "/smoothepi-terms-of-use",
    name: "SmoothepiTermsOfUse",
    component: SmoothepiTermsOfUseView,
    meta: {
      title:
        "スムースエピアプリ 利用規約・プライバシーポリシー | ボディケア（光エステ・脱毛器） | Panasonic",
    },
  },
  {
    path: "/withpb-terms-of-use",
    name: "WithpbTermsOfUse",
    component: FacecareTermsOfUseView,
    meta: {
      titleKey: "termsOfUse.meta.title",
    },
  },
  {
    path: "/doltz-terms-of-use",
    name: "DoltzTermsOfUse",
    component: DoltzTermsOfUseView,
    meta: {
      title:
        "ドルツアプリ 利用規約・プライバシーポリシー | オーラルケア（電動歯ブラシ・水流洗浄器） | Panasonic",
    },
  },
  {
    path: "/plaque-scan-terms-of-use",
    name: "PlaqueTermsOfUse",
    component: PlaqueTermsOfUseView,
    meta: {
      title:
        "プラークスキャンアプリ 利用規約・プライバシーポリシー | オーラルケア（電動歯ブラシ・水流洗浄器） | Panasonic",
    },
  },
  // **注意**: 別アプリの利用規約を追加する際は `to.name !== "PlaqueTermsOfUse" &&`の下にホワイトリストとして追加が必要
  //  追加しない場合は、直接アクセスするとpanasonicドメインからリダイレクトされる際にトップ画面に遷移してしまいます
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  // console.log(`from: ${JSON.stringify(from, null, 4)}`);
  // console.log(`to: ${JSON.stringify(to, null, 4)}`);

  // ========================================
  // アプリからの起動・再起動（トップページ進入時）にセッションを同期
  // ========================================
  if (to.name === "TutorialView") {
    if (to.query.myCareMachineId) {
      // マイケアプラン対象として外部から起動・変更された場合
      sessionStorage.setItem("myCareMachineId", to.query.myCareMachineId);
    } else {
      // 通常モードとして外部から起動（または内部で最初からやり直す）された場合
      sessionStorage.removeItem("myCareMachineId");
    }
  }

  // ========================================
  // 多言語対応のためクエリ調整
  // (main.jsで整えられたQueryが届く。画面遷移中にパラメータが漏れないように維持する。)
  // ========================================
  const newCountry =
    from.query.country || from.query.country || constant.DEFAULT_COUNTRY;
  const defaultLang = store.getUrlLang(constant.DEFAULT_LOCALE);
  const newLang = from.query.lang || to.query.lang || defaultLang;
  const newQuery = { ...to.query };
  let shouldRedirect = false;
  // 国コード
  if (newCountry !== constant.DEFAULT_COUNTRY && !to.query.country) {
    // デフォルト以外を保持しているが、遷移先にない場合付与
    newQuery.country = newCountry;
    shouldRedirect = true;
  } else if (to.query.country === constant.DEFAULT_COUNTRY) {
    // デフォルト値の場合、取り除く
    delete newQuery.country;
    shouldRedirect = true;
  }

  // 言語コード
  if (newLang !== defaultLang && !to.query.lang) {
    // デフォルト以外を保持しているが、遷移先にない場合付与
    newQuery.lang = newLang;
    shouldRedirect = true;
  } else if (to.query.lang === defaultLang) {
    // デフォルト値の場合、取り除く
    delete newQuery.lang;
    shouldRedirect = true;
  }

  if (
    to.name !== "TermsOfUse" &&
    to.name !== "SmoothepiTermsOfUse" &&
    to.name !== "WithpbTermsOfUse" &&
    to.name !== "DoltzTermsOfUse" &&
    to.name !== "PlaqueTermsOfUse" &&
    to.name !== "Maintenance"
  ) {
    if (isMaintenance) {
      if (shouldRedirect) {
        return next({
          name: "Maintenance",
          query: newQuery,
        });
      }
      return next({
        name: "Maintenance",
      });
    }

    if (from.name === undefined && to.name !== "TutorialView") {
      // 利用規約はそのまま開く (チュートリアル画面リンクから新規タブに開く場合、from.name無し)
      if (to.query.accountId) {
        if (shouldRedirect) {
          return next({
            name: "TutorialView",
            query: {
              ...newQuery,
              accountId: to.query.accountId,
            },
          });
        }
        return next({
          name: "TutorialView",
          query: { ...to.query },
        });
      }

      if (shouldRedirect) {
        return next({
          name: "TutorialView",
          query: newQuery,
        });
      }
      return next({
        name: "TutorialView",
      });
    }
  }

  if (shouldRedirect) {
    return next({ ...to, query: newQuery });
  }
  return next();
});

router.afterEach((to) => {
  const { titleKey, title } = to.meta;

  if (titleKey) {
    document.title = i18n.global.t(titleKey);
  } else if (title) {
    document.title = title;
  } else {
    document.title = i18n.global.t("site.title");
  }
});

export default router;
